import { Search, X } from "lucide-react";

export const BRANDS = [
  { key: "orevital", label: "OreVital", icon: "https://uploads.orevital.com/ov-favicon.png" },
  { key: "mindful_minerals", label: "Mindful Minerals", icon: "https://uploads.orevital.com/mm-favicon.png" },
];

const CATEGORY_ORDER = ["Face", "Body", "Face & Body", "Eyes", "Lips", "Hair"];

export function categoriesFor(products) {
  const seen = new Set();
  products.forEach((p) => (p.categories || []).forEach((c) => seen.add(c)));
  return [...seen].sort((a, b) => {
    const ia = CATEGORY_ORDER.indexOf(a), ib = CATEGORY_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
  });
}

export function filterShopProducts(products, { brand, category, query }) {
  const q = (query || "").trim().toLowerCase();
  return products.filter((p) => {
    if (brand && !(p.brands || []).includes(brand)) return false;
    if (category && !(p.categories || []).includes(category)) return false;
    if (q && !(p.name || "").toLowerCase().includes(q)) return false;
    return true;
  });
}

export default function ShopFilterHero({ brand, onBrand, categories, categoryCounts = {}, category, onCategory, query, onQuery, resultCount = null, searchingAllBrands = false }) {
  return (
    <div className="space-y-3">
      <div className="rounded-t-2xl bg-emerald-200/70 px-4 sm:px-5 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5 font-semibold text-zinc-800 tracking-tight" data-testid="brand-strip">
          <img src="https://uploads.orevital.com/mm-favicon.png" alt="Mindful Minerals" className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white object-cover ring-2 ring-white shadow-sm" />
          <span className="whitespace-nowrap">Mindful Minerals</span>
          <span className="text-zinc-400 mx-1">|</span>
          <img src="https://uploads.orevital.com/ov-favicon.png" alt="OreVital" className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white object-cover ring-2 ring-white shadow-sm" />
          <span>OreVital</span>
        </div>
        <div className="hidden sm:block text-xs text-zinc-700">
          Peptide Intelligence™ &amp; Transdermal Enhancement Performance Peptides
        </div>
      </div>

      <div className="rounded-2xl bg-zinc-900 text-white px-5 sm:px-8 pt-6 pb-5" data-testid="shop-filter-hero">
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight leading-tight">
          Meet the <span className="text-emerald-400">Science</span> Behind the Results
        </h2>
        <p className="mt-1.5 text-sm text-zinc-300 max-w-xl">
          <span className="text-emerald-400 font-semibold">Peptide Intelligence™</span> powered by transdermal mineral innovation
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-2" role="tablist" aria-label="Brand">
            {BRANDS.map((b) => {
              const active = brand === b.key;
              return (
                <button
                  key={b.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-label={b.label}
                  title={b.label}
                  onClick={() => onBrand(b.key)}
                  className={`h-12 sm:h-14 rounded-full pl-1 pr-4 flex items-center gap-2 transition-all duration-200 ${
                    active
                      ? "bg-emerald-400 text-zinc-900 shadow-[0_0_0_4px_rgba(52,211,153,0.25)]"
                      : "bg-white/15 text-white opacity-80 hover:opacity-100 hover:bg-white/30"
                  }`}
                  data-testid={`shop-brand-${b.key}`}
                >
                  <img src={b.icon} alt={b.label} className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white object-cover" />
                  <span className="text-sm font-semibold whitespace-nowrap">{b.label}</span>
                </button>
              );
            })}
          </div>

          {categories.length > 0 && (
            <>
              <span className="hidden sm:block h-6 w-px bg-white/15" aria-hidden />
              <div className="flex flex-wrap items-center gap-1.5" data-testid="shop-category-chips">
                {categories.map((c) => {
                  const active = category === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => onCategory(active ? null : c)}
                      className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${
                        active
                          ? "bg-emerald-400 text-zinc-900 border-emerald-400"
                          : "border-white/20 text-zinc-300 hover:border-white/60 hover:text-white"
                      }`}
                      data-testid={`shop-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {c}
                      {categoryCounts[c] != null && (
                        <span className={`ml-1 ${active ? "text-zinc-700" : "text-zinc-500"}`} data-testid={`shop-category-count-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          {categoryCounts[c]}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <label className="relative flex items-center w-full sm:w-auto sm:ml-auto sm:min-w-[200px] sm:flex-1 sm:max-w-[260px]">
            <Search size={14} className="absolute left-3 text-zinc-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => onQuery(e.target.value)}
              placeholder="Search products…"
              className="w-full h-10 rounded-full bg-white/10 border border-white/10 pl-9 pr-9 text-sm text-white placeholder:text-zinc-400 focus:outline-none focus:border-emerald-400 focus:bg-white/15"
              data-testid="shop-search-input"
            />
            {query && (
              <button
                type="button"
                onClick={() => onQuery("")}
                className="absolute right-2 h-6 w-6 rounded-full text-zinc-300 hover:text-white hover:bg-white/10 flex items-center justify-center"
                aria-label="Clear search"
                data-testid="shop-search-clear"
              >
                <X size={12} />
              </button>
            )}
          </label>
        </div>
        {resultCount != null && (
          <div className="mt-3 text-xs text-zinc-400" data-testid="shop-result-count">
            {resultCount} {resultCount === 1 ? "product" : "products"}
            {category ? ` in ${category}` : ""}
            {searchingAllBrands ? " · searching OreVital + Mindful Minerals" : ""}
          </div>
        )}
      </div>
    </div>
  );
}
